import React from "react";

import type { NavigateOptions } from "react-router-dom";

import { useApiErrorActionsContext } from "commons/context/apiError";

import useHttpBase from "commons/hooks/useHttpBase";
import { makeStateApiError, type StateApiErrorType } from "commons/hooks/useNavigateToApiErrorPage";

import HttpError from "commons/utils/errors/HttpError";

import type {
    OptionsType,
    RequestType,
    RequestWithBodyType,
    ResponseType,
} from "commons/commons-fetch/types/http";
import FetchError from "commons/commons-fetch/utils/errors/FetchError";
import { makeUrl } from "commons/commons-fetch/utils/http";

type HttpManagerParamsType<R> = {
    httpCall: ResponseType<R>;
    options?: OptionsType;
};

export type HookParamsType = {
    baseUrl: string;
    uiBaseUrl: string;
    accessTokenGetter: () => string | undefined;
    navigate: (to: string, options?: NavigateOptions) => void;
};

const manageHttpError = (
    options: OptionsType | undefined,
    navigate: (to: string, options?: NavigateOptions) => void,
    uiBaseUrl: string,
    apiErrorActions:
        | { showError: (state: StateApiErrorType) => void; hideError: () => void }
        | undefined,
    error: HttpError,
    reject: (reason?: unknown) => void
) => {
    if (
        options?.manageUnauthorized !== false &&
        (error.responseStatus === "401" || error.responseStatus === "403")
    ) {
        navigate(
            makeUrl("/auth/login", undefined, {
                from: window.location.pathname.substring(uiBaseUrl.length),
            })
        );
    } else if (options?.rejectNotFound === true && error.responseStatus === "404") {
        reject(error);
    } else if (options?.rejectUnprocessableEntity === true && error.responseStatus === "422") {
        reject(error);
    } else if (options?.rejectHttpError === true) {
        reject(error);
    } else if (apiErrorActions !== undefined) {
        apiErrorActions.showError(makeStateApiError(error));
    } else {
        navigate("/error/api", {
            state: makeStateApiError(error),
        });
    }
};

const manageFetchError = (
    options: OptionsType | undefined,
    navigate: (to: string, options?: NavigateOptions) => void,
    error: FetchError,
    reject: (reason?: unknown) => void
) => {
    if (options?.rejectFetchError) {
        reject(error);
    } else {
        navigate("/error/generic", {
            state: {
                xRequestId: (error.request.headers as { [x: string]: string })["X-Request-ID"],
                message: error.error.toString(),
                method: error.request.method,
                url: error.url,
            },
        });
    }
};

function useHttpManagedBase({ baseUrl, uiBaseUrl, accessTokenGetter, navigate }: HookParamsType) {
    const { get, post, patch, put, del } = useHttpBase({ baseUrl, accessTokenGetter });

    const apiErrorActions = useApiErrorActionsContext();

    const httpManager = React.useCallback(
        <R,>({ httpCall, options }: HttpManagerParamsType<R>): ResponseType<R> => {
            const [apiCall, abortController] = httpCall;

            return [
                new Promise((resolve, reject) => {
                    apiCall.then(resolve).catch((error) => {
                        if (error instanceof HttpError) {
                            manageHttpError(
                                options,
                                navigate,
                                uiBaseUrl,
                                apiErrorActions,
                                error,
                                reject
                            );
                        } else if (error instanceof FetchError) {
                            manageFetchError(options, navigate, error, reject);
                        } else {
                            navigate("/error/generic", {
                                state: {
                                    xRequestId: undefined,
                                    message: undefined,
                                },
                            });
                        }
                    });
                }),
                abortController,
            ];
        },
        [navigate, apiErrorActions, uiBaseUrl]
    );

    const getManaged = React.useCallback(
        <P, Q, R>({
            url,
            pathParams,
            queryParams,
            options,
            headers,
        }: RequestType<P, Q>): ResponseType<R> => {
            return httpManager({
                httpCall: get<P, Q, R>({
                    url,
                    pathParams,
                    queryParams,
                    headers,
                    options,
                }),
                options,
            });
        },
        [httpManager, get]
    );

    const delManaged = React.useCallback(
        <P, Q, R>({
            url,
            pathParams,
            queryParams,
            options,
            headers,
        }: RequestType<P, Q>): ResponseType<R> => {
            return httpManager({
                httpCall: del<P, Q, R>({
                    url,
                    pathParams,
                    queryParams,
                    headers,
                    options,
                }),
                options,
            });
        },
        [httpManager, del]
    );

    const postManaged = React.useCallback(
        <P, Q, B, R>({
            url,
            pathParams,
            queryParams,
            body,
            options,
            headers,
        }: RequestWithBodyType<P, Q, B>): ResponseType<R> => {
            return httpManager({
                httpCall: post<P, Q, B, R>({
                    url,
                    pathParams,
                    queryParams,
                    body,
                    headers,
                    options,
                }),
                options,
            });
        },
        [httpManager, post]
    );

    const patchManaged = React.useCallback(
        <P, Q, B, R>({
            url,
            pathParams,
            queryParams,
            body,
            options,
            headers,
        }: RequestWithBodyType<P, Q, B>): ResponseType<R> => {
            return httpManager({
                httpCall: patch<P, Q, B, R>({
                    url,
                    pathParams,
                    queryParams,
                    body,
                    headers,
                    options,
                }),
                options,
            });
        },
        [httpManager, patch]
    );

    const putManaged = React.useCallback(
        <P, Q, B, R>({
            url,
            pathParams,
            queryParams,
            body,
            options,
            headers,
        }: RequestWithBodyType<P, Q, B>): ResponseType<R> => {
            return httpManager({
                httpCall: put<P, Q, B, R>({
                    url,
                    pathParams,
                    queryParams,
                    body,
                    headers,
                    options,
                }),
                options,
            });
        },
        [httpManager, put]
    );

    return React.useMemo(
        () => ({
            get: getManaged,
            del: delManaged,
            post: postManaged,
            patch: patchManaged,
            put: putManaged,
        }),
        [getManaged, delManaged, postManaged, patchManaged, putManaged]
    );
}

export default useHttpManagedBase;
